import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Header } from '../components/Header';
import { ArrowRight, Trophy, Users, Calendar, Star } from 'lucide-react';

export default function Home() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-48">
          <div className="max-w-3xl">
            <p className="font-heading uppercase tracking-widest text-[#D4FF00] text-sm mb-4">
              Entraînement Football
            </p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-7xl text-white leading-tight mb-6">
              Libérez votre<br />
              <span className="text-[#D4FF00]">potentiel</span>
            </h1>
            <p className="text-lg text-white/80 mb-8 max-w-xl">
              Réservez des séances d'entraînement personnalisées avec nos coachs professionnels. 
              Progressez à votre rythme et atteignez vos objectifs.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/trainers">
                <Button 
                  className="bg-[#D4FF00] text-black hover:bg-[#bfe600] h-12 px-8 font-bold uppercase tracking-wide shadow-[0_0_15px_rgba(212,255,0,0.3)] hover:shadow-[0_0_25px_rgba(212,255,0,0.5)] transition-all"
                  data-testid="cta-book-now"
                >
                  Réserver maintenant
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/trainers">
                <Button 
                  variant="outline"
                  className="h-12 px-8 font-medium border-white/30 text-white hover:bg-white/10 hover:text-white"
                  data-testid="cta-view-trainers"
                >
                  Voir nos coachs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 lg:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-heading uppercase tracking-widest text-muted-foreground text-sm mb-4">
              Pourquoi nous choisir
            </p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl">
              L'excellence au service<br />de votre progression
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Trophy,
                title: 'Coachs Certifiés',
                description: 'Des professionnels diplômés avec une expérience de haut niveau.'
              },
              {
                icon: Users,
                title: 'Cours Privés',
                description: 'Séances individuelles pour une progression optimale et personnalisée.'
              },
              {
                icon: Calendar,
                title: 'Flexibilité',
                description: 'Réservez selon vos disponibilités, du lundi au dimanche.'
              },
              {
                icon: Star,
                title: 'Suivi Personnalisé',
                description: 'Programme adapté à votre niveau et vos objectifs.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-xl border bg-background p-6 hover:border-[#D4FF00]/30 transition-colors"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#D4FF00] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-6 h-6 text-black" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1582583642747-45910b2147aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxzb2NjZXIlMjBmaWVsZCUyMGdyYXNzJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzAzMzM5MTJ8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mb-6">
            Prêt à commencer ?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Rejoignez des centaines de joueurs qui ont déjà fait confiance à ProKick Academy 
            pour améliorer leurs compétences.
          </p>
          <Link to="/register">
            <Button 
              className="bg-[#D4FF00] text-black hover:bg-[#bfe600] h-12 px-8 font-bold uppercase tracking-wide shadow-[0_0_15px_rgba(212,255,0,0.3)] hover:shadow-[0_0_25px_rgba(212,255,0,0.5)] transition-all"
              data-testid="cta-get-started"
            >
              Créer mon compte
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="font-heading uppercase tracking-widest text-muted-foreground text-sm mb-2">
              Ils nous font confiance
            </p>
            <h2 className="font-heading font-black text-2xl sm:text-3xl">
              Nos Partenaires
            </h2>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12" data-testid="partners-section">
            {[
              {
                name: 'Real Valladolid',
                logo: '/partners/valladolid.jpeg'
              },
              {
                name: 'Nîmes Olympique',
                logo: '/partners/nimes.jpeg'
              },
              {
                name: 'AS Cannes',
                logo: '/partners/cannes.jpeg'
              },
              {
                name: 'Getafe CF',
                logo: '/partners/getafe.jpeg'
              },
              {
                name: 'AC Milan Primavera',
                logo: '/partners/milan.jpeg'
              }
            ].map((partner, index) => (
              <div 
                key={index}
                className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-xl p-2 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                title={partner.name}
                data-testid={`partner-${index}`}
              >
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="w-full h-full object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#D4FF00] rounded flex items-center justify-center">
                <span className="font-heading font-black text-black text-xs">PK</span>
              </div>
              <span className="font-heading font-bold text-sm">ProKick Academy</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 ProKick Academy. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
